import { IGameState } from 'State/Game/IGameState';
import { GLayout } from 'Utility/layout/LayoutHelper';
import { EventStateHelper } from 'State/Events/EventStateHelper';
import QuestHelper from 'Utility/QuestHelper';
import { MathHelper } from 'Utility/MathHelper';
import TransformComponent from 'Component/Transform';
import { progressBarFillMaxWidth } from 'Prototype/UI/QuestItem';
import TextComponent from 'Component/Render/Text';
import SpriteComponent from 'Component/Render/Sprite';
import Renderable from 'Component/Renderable';
import { IRewardData, getRewardData } from 'Utility/RewardHelper';
import Game from 'Game';
import { getFarmItemThumbnailAssetName, getFarmItemThumbnailPath } from 'System/Farm/FarmHelpers';
import LevelHelper from 'Utility/LevelHelper';
import { getHighestLevelCompleted } from 'State/Player/PlayerLevelHelper';
import GameObject, { GameObjectEvent } from 'GameObject/GameObject';
import { StartAnimationClip } from 'System/Animation/AnimationHelper';
import { Alpha, QuestItemCompleteOutro, SeasonPassPoints } from 'System/Animation/Clip';
import CleanupManager from 'Utility/CleanupManager';
import { PlayerActions } from 'State/Player/PlayerActions';
import { isArray } from 'lodash';
import { report } from 'report';
import DateHelper from 'Utility/DateHelper';
import { AnimationClipEvent } from 'System/Animation/AnimationClip';
import { applyDefaultButtonAnimation } from 'System/UI/UIHelper';
import { FarmStateHelper } from 'State/Player/Farm/FarmStateHelper';
import { UIActions } from 'State/UI/UIActions';
import { PopupID } from 'State/UI/PopupID';
import { EventQuestsPopupLayout } from 'Layouts/Quests/EventQuestsPopupLayout';
import { CurrentEventQuestPopupLayout } from 'Constants/Events/CurrentEvent';
import Interactable from 'Component/Interactable';
import QuestsMenuHandler from './QuestsMenuHandler';
import { IPlayerExpireableQuestData } from 'State/Player/IPlayerState';
import Armature from 'Prototype/Armature';
import { finishedSeasonPass } from 'State/Player/PlayerSeasonPassHelper';
import { getLastPositionMinusProgress } from '../SeasonPass/TractorHelper';
import { SEASON_PASS_TAB_PROGRESS_WIDTH, SeasonPassProgressUpdateAnimation } from './SeasonPassMenuHelper';

export function initButton(button: GameObject, onClick: () => unknown, cleanupManager: CleanupManager) {
    applyDefaultButtonAnimation(button);
    button.on(GameObjectEvent.SELECTED, onClick);
    cleanupManager.add(() => {
        button.off(GameObjectEvent.SELECTED, onClick);
    });
}

export function updateEventData(state: IGameState, layout: GLayout<unknown>): void {
    if (!isEventLayout(layout)) return;

    const eventQuest = EventStateHelper.getActiveQuest(state);
    const canCollect = QuestHelper.canCompleteQuest(eventQuest);

    layout.eventSubLayout.progressBarFill.getComponent(SpriteComponent).texture = canCollect
        ? 'mission_bar_green'
        : 'mission_bar_blue';
}

function onGiftClicked(layout: GLayout<EventQuestsPopupLayout>, rewards: IRewardData[]) {
    const state = Game.store.getState();

    const itemReward = rewards[0];
    const coinReward = rewards[1];
    const SPReward = rewards[2];

    // Item Reward
    const itemSprite = layout.eventSubLayout.itemSprite;
    const itemSpriteComponent = itemSprite.getComponent(SpriteComponent);
    itemSpriteComponent.texture = 'blank';
    let texture: string;
    if (typeof itemReward.value === 'string') {
        if (FarmStateHelper.alreadyHasItem(state.playerState, itemReward.value) && (itemReward.fallback || itemReward.decoFallback)) {
            if (itemReward.decoFallback && !FarmStateHelper.alreadyHasItem(state.playerState, itemReward.decoFallback.value.toString())) {
                texture = getFarmItemThumbnailAssetName(itemReward.decoFallback.value.toString());
                layout.eventSubLayout.itemText.getComponent(TextComponent).text = '';

                if (!Game.resource.existsOrQueued(texture)) {
                    Game.resource.add(texture, getFarmItemThumbnailPath(itemReward.decoFallback.value.toString()));
                }
            }
            else {
                const fallbackData = getRewardData(itemReward.fallback.type);
                texture = fallbackData.texture;
                layout.eventSubLayout.itemText.getComponent(TextComponent).text = `x${itemReward.fallback.value}`;
            }
        }
        else {
            texture = getFarmItemThumbnailAssetName(itemReward.value);
            layout.eventSubLayout.itemText.getComponent(TextComponent).text = '';

            if (!Game.resource.existsOrQueued(texture)) {
                Game.resource.add(texture, getFarmItemThumbnailPath(itemReward.value));
            }
        }
    }
    else {
        texture = itemReward.iconCacheKey;
        layout.eventSubLayout.itemText.getComponent(TextComponent).text = `x${itemReward.value}`;
    }

    Game.resource.wait(texture).then(() => {
        itemSpriteComponent.texture = texture;
    });

    const levelCost = LevelHelper.getLevelCost(getHighestLevelCompleted(state.playerState));

    layout.eventSubLayout.rewardText.getComponent(TextComponent).text = `${Math.round(coinReward.value * levelCost)}`;
    layout.eventSubLayout.rewardSprite.getComponent(SpriteComponent).texture = coinReward.iconCacheKey;
    layout.eventSubLayout.SPPointsText.getComponent(TextComponent).text = `${Math.round(SPReward.value)}`;

    if (finishedSeasonPass(state.playerState)) {
        layout.eventSubLayout.SPPointsText.getComponent(Renderable).visible = false;
        layout.eventSubLayout.SPPointsIcon.getComponent(Renderable).visible = false;
    }

    const overlay = layout.eventSubLayout.overlay;
    const giftDetailsSprite = layout.eventSubLayout.giftDetailsSprite;
    fadeIn(overlay, 0.6);
    fadeIn(giftDetailsSprite);

    Game.ticker.setTimeout(() => {
        overlay.once(GameObjectEvent.SELECTED, () => {
            fadeOut(overlay, 0.6);
            fadeOut(giftDetailsSprite);
        });
    }, 500);
}

function showTooltip(layout: GLayout<EventQuestsPopupLayout>, eventQuest: IPlayerExpireableQuestData, rewards: IRewardData[], questsMenuHandler: QuestsMenuHandler, indexToClaim: number) {
    const state = Game.store.getState();

    const itemReward = rewards[0];
    const coinReward = rewards[1];
    const SPReward = rewards[2];

    // Item Reward
    const itemSprite = layout.eventSubLayout.itemSprite;
    const itemSpriteComponent = itemSprite.getComponent(SpriteComponent);
    itemSpriteComponent.texture = 'blank';
    let texture: string;
    if (typeof itemReward.value === 'string') {
        if (FarmStateHelper.alreadyHasItem(state.playerState, itemReward.value) && (itemReward.fallback || itemReward.decoFallback)) {
            if (itemReward.decoFallback && !FarmStateHelper.alreadyHasItem(state.playerState, itemReward.decoFallback.value.toString())) {
                texture = getFarmItemThumbnailAssetName(itemReward.decoFallback.value.toString());
                layout.eventSubLayout.itemText.getComponent(TextComponent).text = '';

                if (!Game.resource.existsOrQueued(texture)) {
                    Game.resource.add(texture, getFarmItemThumbnailPath(itemReward.decoFallback.value.toString()));
                }
            }
            else {
                const fallbackData = getRewardData(itemReward.fallback.type);
                texture = fallbackData.texture;
                layout.eventSubLayout.itemText.getComponent(TextComponent).text = `x${itemReward.fallback.value}`;
            }
        }
        else {
            texture = getFarmItemThumbnailAssetName(itemReward.value);
            layout.eventSubLayout.itemText.getComponent(TextComponent).text = '';

            if (!Game.resource.existsOrQueued(texture)) {
                Game.resource.add(texture, getFarmItemThumbnailPath(itemReward.value));
            }
        }
    }
    else {
        texture = itemReward.iconCacheKey;
        layout.eventSubLayout.itemText.getComponent(TextComponent).text = `x${itemReward.value}`;
    }

    Game.resource.wait(texture).then(() => {
        itemSpriteComponent.texture = texture;
    });

    const levelCost = LevelHelper.getLevelCost(getHighestLevelCompleted(state.playerState));

    layout.eventSubLayout.rewardText.getComponent(TextComponent).text = `${Math.round(coinReward.value * levelCost)}`;
    layout.eventSubLayout.rewardSprite.getComponent(SpriteComponent).texture = coinReward.iconCacheKey;
    layout.eventSubLayout.SPPointsText.getComponent(TextComponent).text = `${Math.round(SPReward.value)}`;

    const overlay = layout.eventSubLayout.noTextOverlay;
    const giftDetailsSprite = layout.eventSubLayout.giftDetailsSprite;
    fadeIn(overlay, 0.6);
    fadeIn(giftDetailsSprite);

    const giftBox = layout.eventSubLayout.giftTooltipBox[indexToClaim];
    const giftBoxComplete = layout.eventSubLayout.giftTooltipBoxComplete[indexToClaim];
    const giftBoxComponent = giftBox.getComponent(Renderable);
    const giftBoxCompleteComponent = giftBoxComplete.getComponent(Renderable);
    giftBoxComponent.visible = false;
    giftBoxCompleteComponent.visible = true;

    if (finishedSeasonPass(state.playerState)) {
        PlayerActions.claimQuestRewardById(eventQuest.id, true);
        questsMenuHandler.checkCollectableQuests();

        fadeOut(overlay, 0.6, 1000);
        fadeOut(giftDetailsSprite, 1, 1000);
    } else {
        StartAnimationClip(new SeasonPassPoints(layout.eventSubLayout.root, {
            initialDelay: 500,
            questIndex: -1,
            icon: questsMenuHandler.layout.subLayout.TabSPIcon,
            onComplete: () => {
                PlayerActions.claimQuestRewardById(eventQuest.id, true);
                fadeOut(overlay, 0.6);
                fadeOut(giftDetailsSprite);

                const newState = Game.store.getState();
                const lastPosition = getLastPositionMinusProgress(newState.playerState, SPReward.value);
                SeasonPassProgressUpdateAnimation(newState.playerState.seasonPass.ranks, lastPosition.rank, lastPosition.progress, SPReward.value, 0, 300, {
                    root: questsMenuHandler.layout.root,
                    progressFill: questsMenuHandler.layout.subLayout.TabSPProgressBarFill,
                    milestoneText: questsMenuHandler.layout.subLayout.TabSPMilestoneText,
                    progressText: questsMenuHandler.layout.subLayout.TabSPProgressText,
                    progressMaxWidth: SEASON_PASS_TAB_PROGRESS_WIDTH
                }, () => {
                    questsMenuHandler.checkCollectableQuests();
                });
            },
        }));
    }
}

function fadeIn(gameObject: GameObject, endAlpha: number = 1) {
    StartAnimationClip(
        new Alpha(gameObject, {
            startAlpha: 0,
            endAlpha,
            duration: 450,
        })
    );
}

function fadeOut(gameObject: GameObject, startAlpha: number = 1, delay = 0) {
    StartAnimationClip(
        new Alpha(gameObject, {
            startAlpha,
            endAlpha: 0,
            duration: 450,
            delay,
        }).once(AnimationClipEvent.DESTROY, () => {
            gameObject.getComponent(Renderable).visible = false;
        })
    );
}

export function setEventQuestProgress(state: IGameState, layout: GLayout<unknown>) {
    if (!isEventLayout(layout)) return;

    const fill = layout.eventSubLayout.progressBarFill;
    const text = layout.eventSubLayout.progressText;
    const eventQuest = EventStateHelper.getActiveQuest(state);
    const target = QuestHelper.getFinalQuestTarget(
        eventQuest.type,
        eventQuest.tier,
        QuestHelper.getQuestPoolKeyByEventType(eventQuest.eventType)
    );

    // Set bar fill length
    if (eventQuest.progress !== 0) {
        const percProgress = MathHelper.normalize(eventQuest.progress, 0, target);
        fill.getComponent(TransformComponent).width = Math.min(
            MathHelper.lerp(0, progressBarFillMaxWidth, percProgress),
            progressBarFillMaxWidth
        );
    } else {
        fill.getComponent(TransformComponent).width = 0;
    }
    let currentProgress = MathHelper.clamp(eventQuest.progress, 0, target);
    // Set Progress text
    text.getComponent(TextComponent).text = `${currentProgress} / ${target}`;
}
type EventQuestLayout =
    | EventQuestsPopupLayout
    | CurrentEventQuestPopupLayout;

export function isEventLayout(layout: any): layout is GLayout<EventQuestLayout> {
    return (
        layout instanceof EventQuestsPopupLayout ||
        layout instanceof CurrentEventQuestPopupLayout
    );
}

export function createEventLayout(
    state: IGameState,
    layout: GLayout<EventQuestsPopupLayout>,
    cleanupManager: CleanupManager,
    questMenuHandler: QuestsMenuHandler
) {
    if (!isEventLayout(layout)) return;

    let eventQuest = EventStateHelper.getActiveQuest(state);
    const finalTarget = QuestHelper.getFinalQuestTarget(
        eventQuest.type,
        eventQuest.tier,
        QuestHelper.getQuestPoolKeyByEventType(eventQuest.eventType)
    );
    const eventQuestTargets = QuestHelper.getQuestTarget(
        eventQuest.type,
        eventQuest.tier,
        QuestHelper.getQuestPoolKeyByEventType(eventQuest.eventType)
    );

    const backgroundTexture = QuestHelper.getBackgroundTileKey(
        eventQuest.type,
        eventQuest.tier,
        QuestHelper.getQuestPoolKeyByEventType(eventQuest.eventType)
    );

    layout.eventSubLayout.eventBG.getComponent(SpriteComponent).texture = backgroundTexture;

    // Set quest title
    const questTitle = layout.eventSubLayout.missionText;
    const questTitleText = Game.locale.getText(QuestHelper.getTitleKey(eventQuest.type));
    questTitle.getComponent(TextComponent).text = questTitleText;

    // Set progress text
    const progressBarText = layout.eventSubLayout.progressText;
    const progressText = `${eventQuest.progress} / ${finalTarget}`;
    progressBarText.getComponent(TextComponent).text = progressText;

    updateEventData(state, layout);

    const canCollect = QuestHelper.canCompleteQuest(eventQuest);
    if (canCollect) {
        const indexToClaim = QuestHelper.getQuestTargetIndexToClaim(eventQuest);
        questMenuHandler.hasCollectableEventQuest = true;

        const container = layout.eventSubLayout.eventBG;
        const activeContainer = layout.eventSubLayout.activeContainer;
        const completeContainer = layout.eventSubLayout.completedContainer;
        const completeText = layout.eventSubLayout.completeText;

        if (finishedSeasonPass(state.playerState)) {
            layout.eventSubLayout.SPPointsText.getComponent(Renderable).visible = false;
            layout.eventSubLayout.SPPointsIcon.getComponent(Renderable).visible = false;
        }

        // Create check animation
        const checkDBContainer = layout.eventSubLayout.completedDBContainer;
        const check = GameObject.Instantiate(Armature, {
            dragonbonesName: 'quest_complete_animations',
            armatureName: 'check',
            alpha: 0,
            playOnStart: false,
        });
        checkDBContainer.addChild(check);

        const rewards = QuestHelper.getQuestRewards(
            0,
            QuestHelper.getQuestPoolKeyByEventType(eventQuest.eventType)
        );
        if (!isArray(rewards[indexToClaim])) {
            report('Multi target quest with mismatching rewards', {
                once: true,
                sampleRate: 0.01,
                tags: {
                    eventType: eventQuest.eventType,
                    tier: eventQuest.tier,
                },
                fingerprint: 'QuestMenuHandler:onCreate',
            });
            return;
        }
        const targetRewards = rewards[indexToClaim] as IRewardData[];
        StartAnimationClip(
            new QuestItemCompleteOutro(container, {
                initialDelay: 450,
                activeContainer,
                completeContainer,
                completeText,
                check,
            }).once(AnimationClipEvent.DESTROY, () => {
                activeContainer.updateComponent(Renderable, { visible: true, alpha: 1 });
                showTooltip(layout, eventQuest, targetRewards, questMenuHandler, indexToClaim);
                const updatedState = Game.store.getState();
                eventQuest = EventStateHelper.getActiveQuest(updatedState);
            })
        );
    }

    if (isArray(eventQuestTargets)) {
        layout.eventSubLayout.giftTooltipText.forEach((text: any, index: number) => {
            const currentTarget = eventQuestTargets[index];
            const giftText = layout.eventSubLayout.giftTooltipText[index];
            const giftTextComplete = layout.eventSubLayout.giftTooltipTextComplete[index];

            const giftBox = layout.eventSubLayout.giftTooltipBox[index];
            const giftBoxComplete = layout.eventSubLayout.giftTooltipBoxComplete[index];
            const giftBoxComponent = giftBox.getComponent(Renderable);
            const giftBoxCompleteComponent = giftBoxComplete.getComponent(Renderable);

            const giftTextComponent = giftText.getComponent(TextComponent);
            giftTextComponent.text = String(currentTarget);

            const giftTextCompleteComponent = giftTextComplete.getComponent(TextComponent);
            giftTextCompleteComponent.text = String(currentTarget);

            const parentTransform = giftText.parent.getComponent(TransformComponent);
            const parentCompleteTransform = giftTextComplete.parent.getComponent(TransformComponent);
            const percProgress = MathHelper.normalize(currentTarget, 0, finalTarget);

            parentTransform.x = Math.min(
                MathHelper.lerp(0, progressBarFillMaxWidth, percProgress),
                progressBarFillMaxWidth - 15
            );

            parentCompleteTransform.x = Math.min(
                MathHelper.lerp(0, progressBarFillMaxWidth, percProgress),
                progressBarFillMaxWidth - 15
            );

            const rewards = QuestHelper.getQuestRewards(
                0,
                QuestHelper.getQuestPoolKeyByEventType(eventQuest.eventType)
            );
            if (!isArray(rewards[index])) {
                report('Multi target quest with mismatching rewards', {
                    once: true,
                    sampleRate: 0.01,
                    tags: {
                        eventType: eventQuest.eventType,
                        tier: eventQuest.tier,
                    },
                    fingerprint: 'QuestMenuHandler:onCreate',
                });
                return;
            }
            const targetRewards = rewards[index] as IRewardData[];
            if (QuestHelper.hasQuestTargetBeenClaimed(eventQuest, index)) {
                const interactable = giftText.parent.getComponent(Interactable);
                interactable.enabled = false;
                interactable.buttonMode = false;
                giftBoxComponent.visible = false;
                giftBoxCompleteComponent.visible = true;
            }
            giftText.parent.on(GameObjectEvent.SELECTED, () => {
                if (!QuestHelper.hasQuestTargetBeenClaimed(eventQuest, index))
                    onGiftClicked(layout, targetRewards);
            });
            cleanupManager.add(() => {
                giftText.parent.off(GameObjectEvent.SELECTED, () => {
                    onGiftClicked(layout, targetRewards);
                });
            });
        });
    }

    // Set timer
    const timerTextComponent = layout.eventSubLayout.timeText.getComponent(TextComponent);
    timerTextComponent.text = DateHelper.timeTo(DateHelper.now, eventQuest.expiration, 'DD:hh:mm:ss');

    const timerInterval = Game.ticker.setInterval(() => {
        if (!timerTextComponent?.gameObject || timerTextComponent.gameObject.destroyed) {
            return;
        }
        timerTextComponent.text = DateHelper.timeTo(DateHelper.now, eventQuest.expiration, 'DD:hh:mm:ss');
        if ((eventQuest.expiration.getTime() - DateHelper.now.getTime()) < 1000)
            UIActions.hidePopup(PopupID.QUEST_MENU);
    }, 1000);

    cleanupManager.add(() => {
        Game.ticker.clearInterval(timerInterval);
    });

    setEventQuestProgress(state, layout);
}
